import { parseErrorResponseBody } from "../errors/SDKSupernovaError";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Bridge implementation
export class DataBridge {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(conf) {
        var _a, _b, _c, _d;
        this.isPreview = false;
        this.jobId = null;
        this.authToken = conf.accessToken;
        this.apiUrl = conf.apiUrl;
        this.aiApiUrl = conf.aiApiUrl;
        this.apiVersion = conf.apiVersion;
        this.target = conf.target;
        this.customFetchLib = (_a = conf.customFetchLib) !== null && _a !== void 0 ? _a : null;
        this.customProxyLib = (_b = conf.customProxyLib) !== null && _b !== void 0 ? _b : null;
        if (conf.jobId) {
            this.jobId = conf.jobId;
        }
        if (conf.isPreview) {
            this.isPreview = conf.isPreview;
        }
        this.requestHook = conf.requestHook;
        this.debugRequestObserver = (_c = conf.debugRequestObserver) !== null && _c !== void 0 ? _c : null;
        this.debugResponseObserver = (_d = conf.debugResponseObserver) !== null && _d !== void 0 ? _d : null;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Requests
    async delete(endpoint, payload) {
        return this.request(endpoint, payload, "DELETE");
    }
    async get(endpoint) {
        return this.request(endpoint, undefined, "GET");
    }
    async getStreamed(endpoint) {
        return this.streamedRequest(endpoint, undefined, "GET");
    }
    async getAsData(endpoint) {
        return this.request(endpoint, undefined, "GET", true);
    }
    async put(endpoint, payload) {
        return this.request(endpoint, payload, "PUT");
    }
    async post(endpoint, payload) {
        return this.request(endpoint, payload, "POST");
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Requests (result envelope)
    //
    // Most endpoints wrap their payload in a `{ result }` envelope. These helpers
    // unwrap it so request methods can name the data type directly without
    // repeating `{ result: ... }` and a `.result` access at every call site.
    /** GET that unwraps the standard `{ result }` envelope */
    async getResult(endpoint) {
        return (await this.get(endpoint)).result;
    }
    /** GET (data scope) that unwraps the standard `{ result }` envelope */
    async getAsDataResult(endpoint) {
        return (await this.getAsData(endpoint)).result;
    }
    /** POST that unwraps the standard `{ result }` envelope */
    async postResult(endpoint, payload) {
        return (await this.post(endpoint, payload)).result;
    }
    /** PUT that unwraps the standard `{ result }` envelope */
    async putResult(endpoint, payload) {
        return (await this.put(endpoint, payload)).result;
    }
    /** DELETE that unwraps the standard `{ result }` envelope */
    async deleteResult(endpoint, payload) {
        return (await this.delete(endpoint, payload)).result;
    }
    /** Upload that unwraps the standard `{ result }` envelope */
    async uploadResult(endpoint, formData, method = "PUT") {
        return (await this.upload(endpoint, formData, method)).result;
    }
    async postStreamed(endpoint, payload) {
        return this.streamedRequest(endpoint, payload, "POST");
    }
    async upload(endpoint, formData, method = "PUT") {
        var _a;
        const config = await this.buildRequestConfig(method, undefined);
        const proxyAgent = this.getProxyAgent();
        const invoker = this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, { requestUrl: endpoint, requestMethod: method });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                const init = {
                    method: config.method,
                    headers: config.headers,
                    body: formData,
                    agent: proxyAgent,
                };
                invoker(endpoint, init)
                    .then(async (response) => {
                    return this.handleResponse(response, endpoint);
                })
                    .then((jsonResponse) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(jsonResponse);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }
    async request(endpoint, payload, method, asData = false) {
        var _a;
        const config = await this.buildRequestConfig(method, "application/json");
        const proxyAgent = this.getProxyAgent();
        const invoker = this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
            requestUrl: endpoint,
            requestMethod: method,
            asData,
        });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                const init = {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                };
                invoker(endpoint, init)
                    .then(async (response) => {
                    return this.handleResponse(response, endpoint, asData);
                })
                    .then((response) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(response);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }
    async streamedRequest(endpoint, payload, method, asData = false) {
        var _a;
        const config = await this.buildRequestConfig(method, "text/plain");
        const proxyAgent = this.getProxyAgent();
        const invoker = this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
            requestUrl: endpoint,
            requestMethod: method,
            isStreamed: true,
            asData,
        });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                const init = {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                };
                invoker(endpoint, init)
                    .then(async (response) => {
                    return this.handleStreamedResponse(response, endpoint);
                })
                    .then((response) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(response);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }
    async handleResponse(response, endpoint, asData = false) {
        if (!response.ok) {
            // Read the body as text first so a non-JSON error page does not throw
            const rawText = await response.text();
            throw parseErrorResponseBody(response.status, endpoint, rawText);
        }
        if (asData) {
            return response.arrayBuffer();
        }
        const json = await response.json();
        return json;
    }
    async handleStreamedResponse(response, endpoint) {
        var _a;
        if (!response.ok) {
            // Read the body as text first so a non-JSON error page does not throw
            const rawText = await response.text();
            throw parseErrorResponseBody(response.status, endpoint, rawText);
        }
        const reader = (_a = response.body) === null || _a === void 0 ? void 0 : _a.getReader();
        if (!reader) {
            throw new Error("Failed to get reader from response, likely using a non-streaming endpoint with streaming handler");
        }
        return reader;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Request configuration
    async buildRequestConfig(method, contentType) {
        var _a;
        const headers = {};
        if (contentType) {
            headers["Content-Type"] = contentType;
        }
        if (this.isPreview) {
            headers["x-sn-exporter-preview"] = "true";
        }
        if (this.jobId) {
            headers["x-sn-export-job"] = this.jobId;
        }
        // Server-side secret injection
        if ((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.SUPERNOVA_API_SECRET) {
            headers["X-SN-API-SECRET"] = process.env.SUPERNOVA_API_SECRET;
        }
        const config = { method, headers };
        let skipAuth = false;
        if (this.requestHook) {
            const hookResult = await this.requestHook(config);
            if (hookResult && hookResult.skipDefaultAuth) {
                skipAuth = true;
            }
        }
        if (!skipAuth || !this.authToken) {
            const token = this.authToken;
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    }
    getProxyAgent() {
        return this.customProxyLib ? this.customProxyLib : undefined;
    }
    getFetchInvoker() {
        return this.customFetchLib ? this.customFetchLib : fetch;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Endpoints
    getAIEndpoint(fragment) {
        return `${this.aiApiUrl}/${fragment}`;
    }
    getApiEndpoint(fragment) {
        return `${this.apiUrl}/${fragment}`;
    }
    getBaseEndpoint(fragment) {
        if (fragment) {
            return `${this.apiUrl}/${fragment}`;
        }
        return this.apiUrl;
    }
    getPublicEndpoint(fragment) {
        return `${this.apiUrl}/public/${fragment}`;
    }
    getAclEndpoint(resource) {
        if (!resource) {
            return this.getPublicEndpoint("acl");
        }
        return `${this.getPublicEndpoint("acl")}/${resource}`;
    }
    getMCPStreamEndpoint(fragment) {
        if (fragment) {
            return this.getApiEndpoint(`mcp/streams/${fragment}`);
        }
        return this.getApiEndpoint("mcp/streams");
    }
    getUserEndpoint(userId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}`;
    }
    getUserWorkspaceEndpoint(userId, wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}/workspaces/${wsId}`;
    }
    getWorkspaceEndpoint(wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/workspaces/${wsId}`;
    }
    getWorkspaceIntegrationCredentialEndpoint(wsId, integrationId, credentialId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}/credentials/${credentialId}`;
    }
    getWorkspaceIntegrationEndpoint(wsId, integrationId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}`;
    }
    getWorkspaceIntegrationGitProvidersEndpoint(wsId, integrationId, operation) {
        return `${this.getWorkspaceIntegrationEndpoint(wsId, integrationId)}/git/${operation}`;
    }
    getDesignSystemEndpoint(dsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/design-systems/${dsId}/${fragment}`;
        }
        return `${this.apiUrl}/design-systems/${dsId}`;
    }
    getVersionEndpoint(dsId, dsVersionId, fragment) {
        if (fragment) {
            return this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}/${fragment}`);
        }
        return this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}`);
    }
    getVersionCreationJobEndpoint(dsId, jobId) {
        return this.getDesignSystemEndpoint(dsId, `versions/job/${jobId}`);
    }
    getVersionCreationJobsEndpoint(dsId) {
        return this.getDesignSystemEndpoint(dsId, `versions/jobs`);
    }
}
